import { defineStore } from 'pinia';
import { ref, watch } from 'vue';
import config from '@/config/ConfigProvider';
import type { Keyword } from '@ilteducation/content-ref-data-types';
import useKeywords from '@ilteducation/content-ref-data/src/use-keywords';
import axios from 'axios';
import type { GetAppTranslationsV1ResponsePayload } from '@ilteducation/babelfish-types';

type KeywordPartTranslation = {
  [lang: string]: {
    translation: string;
    translationSource: string;
  };
};
export type KeywordTranslation = Record<string, KeywordPartTranslation>;
export type KeywordWithTranslations = Keyword & {
  supportedLanguages: string[];
  translations: KeywordTranslation;
};

type BabelfishKeywords = {
  [keywordId: string]: KeywordWithTranslations;
};

// FIXME: temporary hardcoded list to filter out languages used in learn-it but not manabua
const supportedManabuaKeywordsLanguages = [
  'ar',
  'de',
  'en-gb',
  'es',
  'prs',
  'fr',
  'ru',
  'sv',
  'ukr',
  'so',
  'ti',
  'fi',
  'no-nb',
  'pl',
  'th',
];

const useKeywordsStore = defineStore('keywords', () => {
  const cdnDomain = config.value('cdnDomain');
  const contentRefKeywords = useKeywords();
  const isLoading = ref<{
    [keywordId: string]: boolean;
  }>({});
  const isLoaded = ref<{
    [keywordId: string]: boolean;
  }>({});

  const keywords = ref<BabelfishKeywords>({});

  const getByKey = (name: string) => Object.values(keywords.value).find((keyword) => keyword.key === name);

  const getBabelfishTranslations = async (keywordIds: string[]) => {
    keywordIds.forEach((id) => {
      isLoading.value[id] = true;
    });
    return axios<GetAppTranslationsV1ResponsePayload>(
      `${config.value('babelfishUrl')}/translations?sourceIds=${keywordIds.join(',')}`,
    ).then((res) => res.data);
  };

  const getKeywordTranslations = async (keywordIds: string[]): Promise<KeywordWithTranslations[]> => {
    const notLoadedKeywords: string[] = [];
    const loadedOrLoading = keywordIds
      .map((id) => {
        if (isLoaded.value[id]) {
          return Promise.resolve(keywords.value[id]);
        }
        if (isLoading.value[id]) {
          return new Promise<KeywordWithTranslations>((resolve) => {
            const unwatch = watch(
              () => isLoaded.value[id],
              (loaded) => {
                if (loaded) {
                  unwatch();
                  resolve(keywords.value[id]);
                }
              },
            );
          });
        }
        notLoadedKeywords.push(id);
        return undefined;
      })
      .filter((promise): promise is Promise<KeywordWithTranslations> => promise !== undefined);

    const babelfishTranslations =
      notLoadedKeywords.length > 0 ? await getBabelfishTranslations(notLoadedKeywords) : undefined;
    if (babelfishTranslations) {
      await useKeywords().byIds(Object.keys(babelfishTranslations));
    }
    const notLoaded = babelfishTranslations
      ? Object.entries(babelfishTranslations).map(async ([key, value]) => {
          let supportedLanguages = Object.keys(value.title || {});
          supportedLanguages = supportedLanguages.filter((sl) => supportedManabuaKeywordsLanguages.includes(sl));
          const baseKeyword = (await contentRefKeywords.byId(key)) as Keyword;
          const keywordWithTranslations = {
            ...baseKeyword,
            supportedLanguages,
            translations: value,
          };
          keywords.value[key] = keywordWithTranslations;
          isLoaded.value[key] = true;
          return keywordWithTranslations;
        })
      : [];

    notLoadedKeywords.forEach((id) => {
      isLoading.value[id] = false;
    });

    return Promise.all([...loadedOrLoading, ...notLoaded]);
  };

  const loadByKey = async (key: string) => {
    const keyword = getByKey(key);
    if (keyword) {
      return keyword;
    }
    const keywordId = (await contentRefKeywords.byKey(key))?.id;
    if (!keywordId) {
      return undefined;
    }
    const translations = await getKeywordTranslations([keywordId]);
    return translations && translations.length > 0 ? translations[0] : undefined;
  };

  const getKeywordImageUrl = (keyword: Keyword, width?: number, height?: number) => {
    if (!keyword.imageId) {
      return '';
    }
    const base = `${cdnDomain}/images/${keyword.imageId}/image.jpeg`;
    const qs: string[] = [];
    // Get the image in double resolution for highres displays if width or height is specified
    if (width) {
      qs.push(`width=${width * 2}`);
    }
    if (height) {
      qs.push(`height=${height * 2}`);
    }
    if (qs.length > 0) {
      return `${base}?${qs.join('&')}`;
    }
    return base;
  };

  const translateKeyword = (keyword: KeywordWithTranslations, lang: string) => ({
    title: keyword.translations?.title?.[lang]?.translation || '',
    description: keyword.translations?.description?.[lang]?.translation || '',
  });

  return {
    getKeywordTranslations,
    isLoaded,
    isLoading,
    keywords,
    getByKey,
    getKeywordImageUrl,
    loadByKey,
    translateKeyword,
  };
});

export default useKeywordsStore;
